import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Loader2, ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const PRESETS = [0.5, 5, 10, 25, 50, 100];

export default function DonationWidget({ minAmount = 0.5, onSuccess }) {
  const [selected, setSelected] = useState(10);
  const [customAmount, setCustomAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const effectiveAmount = useMemo(() => {
    if (customAmount !== "") {
      const n = parseFloat(customAmount);
      return isNaN(n) ? 0 : n;
    }
    return selected;
  }, [selected, customAmount]);

  const isValid = effectiveAmount >= minAmount;

  const handleSelect = (amt) => {
    setSelected(amt);
    setCustomAmount("");
    setError("");
  };

  const handleCustomChange = (e) => {
    const v = e.target.value.replace(/[^0-9.]/g, "");
    setCustomAmount(v);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!isValid) {
      setError(`Minimum donation is $${minAmount.toFixed(2)}`);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        amount: effectiveAmount,
        donor_name: name.trim() || "Anonymous",
        message: message.trim() || "",
      };
      if (email.trim()) payload.donor_email = email.trim();

      const res = await api.post("/donations/create", payload);
      const { invoice_url, donation_id } = res.data;

      toast.success("Redirecting to secure payment...");
      if (onSuccess) onSuccess();

      // Small delay for UX, then redirect
      setTimeout(() => {
        if (invoice_url) {
          // Open NOWPayments invoice (card + crypto checkout)
          window.location.href = invoice_url;
        } else {
          window.location.href = `/success?d=${donation_id}`;
        }
      }, 500);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Payment could not be initiated. Please try again.";
      setError(typeof msg === "string" ? msg : "Payment error");
      toast.error(typeof msg === "string" ? msg : "Payment error");
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="donation-widget"
      className="bg-white rounded-3xl p-8 md:p-10 border border-stone-border/60 shadow-medium max-w-2xl mx-auto"
    >
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Presets */}
        <div>
          <label className="block text-sm font-medium text-ink-body mb-4">Select donation amount</label>
          <div className="grid grid-cols-3 gap-3">
            {PRESETS.map((amt) => {
              const active = customAmount === "" && selected === amt;
              return (
                <button
                  key={amt}
                  type="button"
                  onClick={() => handleSelect(amt)}
                  data-testid={`donation-preset-${amt}`}
                  className={`rounded-xl py-4 text-lg font-medium border transition-all duration-200 ${
                    active
                      ? "bg-terracotta border-terracotta text-white shadow-soft"
                      : "bg-white border-stone-border text-ink-body hover:border-terracotta hover:text-terracotta"
                  }`}
                >
                  ${amt}
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom amount */}
        <div>
          <label htmlFor="custom-amount" className="block text-sm font-medium text-ink-body mb-2">Or enter a custom amount</label>
          <div className="relative flex items-center">
            <span className="absolute left-4 text-ink-muted font-medium text-lg">$</span>
            <input
              id="custom-amount"
              type="text"
              inputMode="decimal"
              placeholder={`${minAmount.toFixed(2)} or more`}
              value={customAmount}
              onChange={handleCustomChange}
              data-testid="custom-amount-input"
              className="w-full pl-8 pr-4 py-4 rounded-xl border border-stone-border bg-white text-lg text-ink focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta outline-none transition-all"
            />
          </div>
          <p className="mt-2 text-xs text-ink-muted">Minimum donation: ${minAmount.toFixed(2)}</p>
        </div>

        {/* Donor fields */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="donor-name" className="block text-sm font-medium text-ink-body mb-2">Your name <span className="text-ink-muted">(optional)</span></label>
            <input
              id="donor-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Anonymous"
              data-testid="donor-name-input"
              className="w-full px-4 py-3 rounded-xl border border-stone-border bg-white text-ink focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta outline-none transition-all"
            />
          </div>
          <div>
            <label htmlFor="donor-email" className="block text-sm font-medium text-ink-body mb-2">Email <span className="text-ink-muted">(for receipt)</span></label>
            <input
              id="donor-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="donor-email-input"
              className="w-full px-4 py-3 rounded-xl border border-stone-border bg-white text-ink focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta outline-none transition-all"
            />
          </div>
        </div>

        <div>
          <label htmlFor="donor-message" className="block text-sm font-medium text-ink-body mb-2">Message <span className="text-ink-muted">(optional)</span></label>
          <textarea
            id="donor-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="A note for the residents..."
            data-testid="donor-message-input"
            className="w-full px-4 py-3 rounded-xl border border-stone-border bg-white text-ink focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta outline-none transition-all resize-none"
          />
        </div>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              data-testid="donation-error"
              className="flex items-start gap-2 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700"
            >
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Submit */}
        <button
          type="submit"
          disabled={!isValid || loading}
          data-testid="submit-donation-btn"
          className="w-full bg-terracotta hover:bg-terracotta-hover text-white rounded-full py-5 font-medium text-lg transition-all duration-300 shadow-soft hover:shadow-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 group"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Preparing your donation...
            </>
          ) : (
            <>
              <Heart className="w-5 h-5" />
              Donate ${effectiveAmount.toFixed(2)}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>

        <p className="text-center text-xs text-ink-muted">
          Secured by NOWPayments. Pay with card or 75+ cryptocurrencies. <br/>
          Funds settle directly to the charity's Binance wallet in USDT.
        </p>
      </form>
    </motion.div>
  );
}
